import { fmtDayMonth } from '../../shared/ui/flight_format.js';
import { genUiT, type GenUiKey } from '../../shared/ui/i18n.js';
import {
  freeCancellationUntilOfWarning,
  HOTEL_RATE_WARNING_FREE_CANCELLATION_ENDED,
  HOTEL_RATE_WARNING_NON_REFUNDABLE,
} from '@voll-intelligence/genui-contract';

/** O fornecedor manda o motivo como CÓDIGO (`breakfast_changed`), não como
 *  frase — e o código estava indo cru para a tela. Traduzimos os que
 *  conhecemos; o resto cai no texto do fornecedor, porque esconder um aviso de
 *  tarifa é pior do que mostrá-lo feio. Código novo entra aqui e no i18n.
 *
 *  Dois códigos vêm crus do hotels-api (`booking.conditions.validate.
 *  service.js`, develop 21cbe50e): `breakfast_changed` e `refundable_changed`.
 *  Os de cancelamento são do modal-session, que reescreve a frase de prazo do
 *  fornecedor (`hotel_rate_warning_codes.ts`); o valor total alterado segue
 *  chegando como frase localizada e cai no fallback do texto. */
const WARNING_REASON_KEYS: Readonly<Record<string, GenUiKey>> = {
  breakfast_changed: 'hotelRateWarning.reason.breakfast_changed',
  refundable_changed: 'hotelRateWarning.reason.refundable_changed',
  [HOTEL_RATE_WARNING_NON_REFUNDABLE]: 'hotelRateWarning.reason.non_refundable',
  [HOTEL_RATE_WARNING_FREE_CANCELLATION_ENDED]: 'hotelRateWarning.reason.free_cancellation_ended',
};

function reasonText(message: string, locale: string | null | undefined): string {
  const until = freeCancellationUntilOfWarning(message.trim());
  if (until) {
    return genUiT(locale, 'hotelRateWarning.reason.free_cancellation_until', {
      date: fmtDayMonth(until, locale),
    });
  }
  const key = WARNING_REASON_KEYS[message.trim().toLowerCase()];
  return key ? genUiT(locale, key) : message;
}

/**
 * Os motivos do aviso de tarifa em texto humano.
 *
 * Mora fora do card porque a MESMA lista aparece em duas telas: o card
 * `hotel_rate_warning` do chat e o aviso de preço mudado da bancada
 * determinística (`HotelBancadaView`), que recebe os mesmos códigos do
 * fornecedor pela resposta de `POST /hotel/quote`. Duas cópias do mapa
 * significavam um código novo traduzido numa tela e cru na outra.
 *
 * Puro e sem React de propósito — o host da bancada não renderiza dentro do
 * `GenUiConfigProvider`, então não tem o hook; passa o locale que já resolve
 * para o resto do kit.
 */
export function hotelRateWarningReasons(
  warnings: readonly string[],
  /** Aceita a string crua do host (`currentLocale()`) — `genUiT` resolve. */
  locale: string | null | undefined,
  /** A tarifa estava DENTRO da política na leitura que a pessoa viu e o quote a
   *  devolveu FORA (#3631). Não é código do fornecedor — quem compara os dois
   *  vereditos é o host —, mas mora na mesma lista para o motivo que mais pesa
   *  (a justificativa passa a ser exigida no checkout) abrir o aviso. */
  options?: { readonly policyTurnedOut?: boolean },
): string[] {
  const reasons = warnings.map((message) => reasonText(message, locale));
  return options?.policyTurnedOut
    ? [genUiT(locale, 'hotelRateWarning.policyTurnedOut'), ...reasons]
    : reasons;
}
